import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { apiClient } from '../../api/client';

// Demo accounts for quick login (development only)
const DEMO_ACCOUNTS = [
  { label: 'Admin', email: 'admin@nosetea.com', password: 'Admin@123', color: 'bg-blue-500' },
  { label: 'Manager', email: 'manager@nosetea.com', password: 'Manager@123', color: 'bg-green-500' },
  { label: 'Supervisor', email: 'supervisor@nosetea.com', password: 'Super@123', color: 'bg-teal-500' },
  { label: 'POS', email: 'pos@nosetea.com', password: 'Pos@1234', color: 'bg-purple-500' },
  { label: 'Warehouse', email: 'warehouse@nosetea.com', password: 'Warehouse@123', color: 'bg-orange-500' },
  { label: 'Purchasing', email: 'purchasing@nosetea.com', password: 'Purchase@123', color: 'bg-pink-500' },
];

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser, setTokens } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleQuickLogin = (demoEmail: string, demoPassword: string) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/login', {
        email,
        password,
      });

      const { user, tokens } = response.data.data;

      setUser(user);
      setTokens(tokens.accessToken, tokens.refreshToken);
      navigate('/pos');
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
      <div className="w-full max-w-md p-8 bg-card rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary">Nose Tea</h1>
          <p className="text-muted-foreground mt-2">POS System</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              {t('auth.password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive text-destructive text-sm rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {loading ? t('common.loading') : t('auth.loginButton')}
          </button>
        </form>

        {/* Quick Login (Demo) */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-center text-gray-500 mb-3">
            Quick Login (Demo):
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.email}
                type="button"
                onClick={() => handleQuickLogin(account.email, account.password)}
                className={`px-3 py-1.5 text-xs font-medium text-white rounded-full ${account.color} hover:opacity-80 transition-opacity`}
              >
                {account.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
